import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { useEffect, useRef, useState } from 'react'

import { formatTimeLabel } from '@/lib/time'

// Touch devices get the native OS time picker; pointer devices (desktop) get a
// custom hour/minute popover.
function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia('(pointer: coarse)')
    const update = () => setCoarse(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return coarse
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
const pad = (n: number) => String(n).padStart(2, '0')

function stop(e: { stopPropagation: () => void }) {
  e.stopPropagation()
}

export function TimeField({
  value,
  onChange,
  className,
  align = 'start',
  ariaLabel,
}: {
  value: string // "HH:MM"
  onChange: (next: string) => void
  className?: string
  align?: 'start' | 'center' | 'end'
  ariaLabel?: string
}) {
  const coarse = useCoarsePointer()
  const [h = 9, m = 0] = value.split(':').map(Number)

  if (coarse) {
    // Native picker: our styled text with a transparent time input overlaid.
    return (
      <span
        className={cn('relative inline-flex cursor-pointer items-center', className)}
        onMouseDown={stop}
        onClick={(e) => {
          e.stopPropagation()
          try {
            e.currentTarget.querySelector<HTMLInputElement>('input')?.showPicker()
          } catch {
            /* picker already open */
          }
        }}
      >
        {formatTimeLabel(value)}
        {/* pointer-events-none so a swipe over the field reaches the card; the
            picker is opened programmatically from the span's onClick. */}
        <input
          type="time"
          lang="en-GB"
          aria-label={ariaLabel}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="time-overlay pointer-events-none absolute inset-0 h-full w-full opacity-0"
        />
      </span>
    )
  }

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={ariaLabel}
            className={cn('cursor-pointer', className)}
            onMouseDown={stop}
            onClick={stop}
          />
        }
      >
        {formatTimeLabel(value)}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        side="bottom"
        className="w-auto p-0"
        onClick={stop}
        onMouseDown={stop}
      >
        <div className="flex h-44 text-xs">
          <TimeColumn
            items={HOURS}
            selected={h}
            onPick={(hr) => onChange(`${pad(hr)}:${pad(m)}`)}
          />
          <div className="w-px bg-border/40" />
          <TimeColumn
            items={MINUTES}
            selected={m}
            onPick={(mn) => onChange(`${pad(h)}:${pad(mn)}`)}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

function TimeColumn({
  items,
  selected,
  onPick,
}: {
  items: number[]
  selected: number
  onPick: (n: number) => void
}) {
  const activeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'center' })
  }, [])
  return (
    <div className="flex flex-col overflow-y-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((n) => {
        const active = n === selected
        return (
          <button
            key={n}
            ref={active ? activeRef : undefined}
            type="button"
            onClick={() => onPick(n)}
            className={cn(
              'cursor-pointer rounded px-3 py-1.5 text-center font-medium tabular-nums transition-colors hover:bg-muted',
              active ? 'bg-primary/15 text-primary-text' : 'text-foreground',
            )}
          >
            {pad(n)}
          </button>
        )
      })}
    </div>
  )
}
